import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to sign in.');
      }

      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_role', data.role);
      setTimeout(() => {
        window.location.replace('/');
      }, 500);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);

  return (
    <>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #F8F9FA; font-family: 'Inter', -apple-system, sans-serif; overflow-x: hidden; }

        .login-page-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 24px;
          position: relative;
        }

        .top-left-logo {
          position: absolute;
          top: 32px;
          left: 32px;
          height: 48px;
          width: auto;
          transition: all 0.3s;
        }

        .login-card {
          width: 100%;
          max-width: 440px;
          background: #FFFFFF;
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          border: 1px solid #E5E7EB;
          overflow: hidden;
          animation: fadeInUp 0.6s ease-out;
          position: relative;
          z-index: 5;
        }

        .card-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 36px 40px;
          text-align: center;
          color: #FFFFFF;
        }

        .header-title { font-size: 1.5rem; font-weight: 700; text-shadow: 0 2px 10px rgba(0,0,0,0.1); }

        .card-body { padding: 48px 40px; }
        .login-form { display: flex; flex-direction: column; gap: 24px; }
        .form-group { position: relative; }
        .form-label { font-size: 13px; font-weight: 700; color: #374151; display: block; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }

        .input-wrapper { position: relative; display: flex; align-items: center; }
        .input-icon { position: absolute; left: 16px; color: #9CA3AF; pointer-events: none; }
        .form-input { width: 100%; padding: 14px 16px 14px 48px; background: #F9FAFB; border: 2px solid #E5E7EB; border-radius: 12px; outline: none; font-size: 15px; font-weight: 500; color: #111827; transition: all 0.3s ease; }
        .form-input:focus { border-color: #667eea; box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1); background: #fff; }

        .password-toggle { position: absolute; right: 14px; background: none; border: none; cursor: pointer; color: #6B7280; padding: 8px; z-index: 10; }

        .login-button {
          width: 100%; padding: 16px; font-size: 16px; font-weight: 700; color: #FFFFFF;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px; border: none; cursor: pointer; transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3); text-transform: uppercase; letter-spacing: 1px;
        }
        .login-button:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 15px 40px rgba(102, 126, 234, 0.4); }
        .login-button:disabled { opacity: 0.7; cursor: not-allowed; }

        .error-message { background: #FEF2F2; color: #991B1B; padding: 14px; border-radius: 12px; text-align: center; font-weight: 600; font-size: 14px; margin-top: 20px; border: 1px solid #FECACA; }

        /* --- SIGNATURE STYLING (Subtle in Bottom Right Corner) --- */
        .signature {
          position: fixed;
          bottom: 12px;
          right: 12px;
          font-size: 11px;
          color: #D1D5DB; /* Very faint */
          font-weight: 500;
          pointer-events: none;
          z-index: 100;
          opacity: 0.6;
        }

        /* Responsive */
        @media (max-width: 640px) {
          .top-left-logo { position: relative; top: 0; left: 0; margin-bottom: 32px; height: 36px; }
          .login-page-container { padding: 16px; }
          .login-card { border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.06); }
          .card-header { padding: 28px 24px; }
          .header-title { font-size: 1.25rem; }
          .card-body { padding: 32px 24px; }
        }
      `}</style>

      <div className="login-page-container">
        <img src="/logo.png" alt="Ethan's Logo" className="top-left-logo" />

        <div className="login-card">
          <div className="card-header">
            <h1 className="header-title">Referral Portal Control Panel</h1>
          </div>
          <div className="card-body">
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <div className="input-wrapper">
                  <Mail size={18} className="input-icon" />
                  <input type="email" className="form-input" placeholder="Enter email address" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="input-wrapper">
                  <Lock size={18} className="input-icon" />
                  <input type={passwordVisible ? "text" : "password"} className="form-input" placeholder="Enter password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" onClick={togglePasswordVisibility} className="password-toggle">
                    {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Logging in...' : 'Sign In'}
              </button>
            </form>
            {error && <div className="error-message">{error}</div>}
          </div>
        </div>

        <div className="signature">Created by ProXy</div>
      </div>
    </>
  );
};

export default LoginPage;